/**
 * parseTTRCode — right-split parser for AuctionSAAS TTR codes.
 *
 * A TTR code identifies a single registered collectible:
 *
 *     <boardId>-<position>-<condition>-<holder>-<copy>
 *
 * The trailing four segments (position, condition, holder, copy) are ALWAYS
 * single, hyphen-free tokens. The boardId, however, can itself contain hyphens:
 *
 *     7677TOPPSBB-001-RAW-SCHMIDT-001            boardId = "7677TOPPSBB"
 *     TTRG-HI-001-001-UNC-SCHMIDT-001            boardId = "TTRG-HI-001"
 *     TTRG-MORGAN-001-001-VF-SCHMIDT-001         boardId = "TTRG-MORGAN-001"
 *
 * Because of that, a naive left-to-right `split('-')` with positional indexing
 * mis-parses every Hawaii and Morgan code. We instead split on '-' and pop the
 * last four segments from the RIGHT; whatever remains (re-joined with '-') is
 * the boardId. This is the "right-split" pattern documented in the recon.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * DUPLICATION NOTICE (TD-28):
 * This utility is intentionally duplicated between the CRA app
 * (buddy-apps/whitelabelbuddy/src/utils/ttr/parseTTRCode.ts) and the DealBuddy
 * Cloud Function (dealbuddy-y3gqu/functions/.../parseTTRCode.js) because the
 * monorepo is NOT an npm/pnpm workspace — buddy-apps/* are independent apps with
 * separate deploys and no shared package. There is no `@auctionsaas/shared` to
 * import from today.
 *
 * The two copies MUST stay byte-for-byte equivalent in behavior. If you change
 * the parsing rules here, change the Cloud Function copy in the same PR.
 * Unifying these into a shared package is filed as TD-28.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export interface ParsedTTRCode {
  /** The full, normalized (trimmed + upper-cased) TTR code. */
  ttrCode: string;
  /** Board identifier — may contain hyphens (e.g. "TTRG-MORGAN-001"). */
  boardId: string;
  /** Position token within the board (e.g. "001"). Zero-padding preserved. */
  position: string;
  /** Condition grade token (e.g. "RAW", "VF", "UNC"). */
  condition: string;
  /** Holder entity token (e.g. "SCHMIDT"). */
  holder: string;
  /** Copy number token for this position/holder (e.g. "001"). */
  copy: string;
}

/** Minimum segment count: boardId(≥1) + position + condition + holder + copy. */
const MIN_SEGMENTS = 5;

/**
 * Parse a TTR code into its components using right-split semantics.
 *
 * @throws {Error} if the code is missing, non-string, or has fewer than 5 segments.
 */
export function parseTTRCode(code: string): ParsedTTRCode {
  if (!code || typeof code !== 'string') {
    throw new Error('Invalid TTR code: missing or non-string');
  }

  const normalized = code.trim().toUpperCase();

  if (normalized.length === 0) {
    throw new Error('Invalid TTR code: empty string');
  }

  const parts = normalized.split('-');

  if (parts.length < MIN_SEGMENTS) {
    throw new Error(
      `Invalid TTR code format: expected at least ${MIN_SEGMENTS} segments, got ${parts.length}`,
    );
  }

  // Pop the trailing four single-token fields from the right.
  const copy = parts.pop() as string;
  const holder = parts.pop() as string;
  const condition = parts.pop() as string;
  const position = parts.pop() as string;

  // Whatever remains is the boardId — re-join so internal hyphens survive.
  const boardId = parts.join('-');

  // Guard against empty tokens produced by leading/trailing/double hyphens
  // (e.g. "BOARD--001-RAW-SCHMIDT-001" or "-001-RAW-SCHMIDT-001").
  if (!boardId || !position || !condition || !holder || !copy) {
    throw new Error('Invalid TTR code: one or more segments are empty');
  }

  return { ttrCode: normalized, boardId, position, condition, holder, copy };
}

export default parseTTRCode;
